import React from 'react';
import { Box, List, ListItem, ListItemButton, ListItemText, Radio, Typography } from '@mui/material';
import Check from '@mui/icons-material/Check';

export type NotebookPickerEntry = {
  id: string;
  name: string;
  description?: string | null;
};

const radioUnchecked = (
  <Box
    sx={{
      width: 22,
      height: 22,
      borderRadius: '50%',
      border: '2px solid #a1a1aa',
      bgcolor: 'rgba(24, 24, 27, 0.6)',
      boxSizing: 'border-box',
    }}
  />
);

const radioChecked = (
  <Box
    sx={{
      width: 22,
      height: 22,
      borderRadius: '50%',
      bgcolor: '#60a5fa',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '2px solid #60a5fa',
    }}
  >
    <Check sx={{ fontSize: 15, color: '#0a0a0a' }} />
  </Box>
);

export type NotebookPickerListProps = {
  notebooks: NotebookPickerEntry[];
  selectedId: string;
  onSelect: (id: string) => void;
  loading?: boolean;
  emptyMessage?: React.ReactNode;
};

/**
 * Scrollable list of notebooks with a clear radio control per row (filled circle + check when selected).
 */
export function NotebookPickerList({
  notebooks,
  selectedId,
  onSelect,
  loading,
  emptyMessage,
}: NotebookPickerListProps) {
  if (loading) {
    return <Typography sx={{ color: '#a1a1aa' }}>Loading notebooks…</Typography>;
  }
  if (notebooks.length === 0) {
    return (
      <Typography sx={{ color: '#a1a1aa' }}>
        {emptyMessage ?? <>No active notebooks. Switch to &quot;New notebook&quot; to create one.</>}
      </Typography>
    );
  }

  return (
    <List
      dense
      disablePadding
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        border: '1px solid #27272a',
        borderRadius: 1,
        bgcolor: '#09090b',
      }}
    >
      {notebooks.map((nb) => {
        const selected = selectedId === nb.id;
        return (
          <ListItem key={nb.id} disablePadding sx={{ borderBottom: '1px solid #27272a', '&:last-of-type': { borderBottom: 0 } }}>
            <ListItemButton
              selected={selected}
              onClick={() => onSelect(nb.id)}
              aria-label={`Select notebook ${nb.name}`}
              sx={{
                py: 1.25,
                pl: 2.5,
                pr: 2,
                alignItems: 'flex-start',
                '&.Mui-selected': { bgcolor: '#27272a' },
                '&.Mui-selected:hover': { bgcolor: '#3f3f46' },
              }}
            >
              <Radio
                edge="start"
                tabIndex={-1}
                disableRipple
                checked={selected}
                onChange={() => onSelect(nb.id)}
                onClick={(e) => e.stopPropagation()}
                icon={radioUnchecked}
                checkedIcon={radioChecked}
                sx={{
                  p: 0.5,
                  ml: 0.25,
                  mr: 2.25,
                  mt: 0.125,
                  alignSelf: 'flex-start',
                  color: 'transparent',
                  '&.Mui-checked': { color: 'transparent' },
                }}
              />
              <ListItemText
                primary={nb.name}
                secondary={nb.description || undefined}
                primaryTypographyProps={{ sx: { color: '#fafafa', fontWeight: 600 } }}
                secondaryTypographyProps={{
                  sx: { color: '#a1a1aa', whiteSpace: 'normal', wordBreak: 'break-word' },
                }}
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
}
